import { Component } from '@theme/component'
import { useGsap } from '@theme/gsap'
import { useStickerReveal } from '@theme/use-sticker-reveal'

const HorizontalWordsConfig = {
  chars: {
    end: 'left 10%',
    scrub: 0.5,
    start: 'left 90%'
  },
  color: {
    ease: {
      toNormal: 'power2.inOut',
      toVariant: 'sine.out'
    },
    progress: {
      variantRampEnd: 0.12
    }
  },
  random: {
    letterRotation: 60,
    letterY: 500,
    stickerRotation: 60,
    stickerY: 400
  },
  scrollTween: {
    end: '125% bottom',
    mobileStart: '-40% top',
    scrub: 0.5,
    start: '-30% top'
  },
  viewport: {
    mobile: '(width <= 799px)'
  }
}

const isHorizontalWordsMobileViewport = () =>
  globalThis.matchMedia?.(HorizontalWordsConfig.viewport.mobile)?.matches ??
  false

const getHorizontalWordsScrollStart = () =>
  isHorizontalWordsMobileViewport()
    ? HorizontalWordsConfig.scrollTween.mobileStart
    : HorizontalWordsConfig.scrollTween.start

class HorizontalWordsSection extends Component {
  /** @type {{ revert: () => void } | null} */
  #gsapContext = null

  /** @type {{ revert?: () => void }[]} */
  #splitInstances = []

  mounted() {
    void this.#init()
  }

  unmounted() {
    this.#cleanup()
  }

  async #init() {
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
      } catch {}
    }

    if (!this.isConnected) return

    const scope = await useGsap(this, () => this.#buildAnimations(), [
      'DrawSVGPlugin',
      'ScrollTrigger',
      'SplitText'
    ])

    if (!this.isConnected) {
      scope?.context?.revert()
      return
    }

    this.#gsapContext = scope?.context ?? null
  }

  #buildAnimations() {
    const section = this.querySelector('.horizontal-words')
    const text = this.querySelector('.horizontal-words__relative')

    if (!(section instanceof HTMLElement && text instanceof HTMLElement)) return

    const gsap = globalThis.gsap
    const ScrollTrigger = globalThis.ScrollTrigger
    const SplitText = globalThis.SplitText
    const DrawSVGPlugin = globalThis.DrawSVGPlugin
    if (!(gsap && ScrollTrigger)) return

    const heading = section.querySelector('.horizontal-words__h2')
    const hasSplitText = Boolean(SplitText && heading instanceof HTMLElement)

    if (hasSplitText) {
      this.#splitInstances.push(
        SplitText.create(heading, {
          charsClass: 'letter',
          type: 'chars'
        })
      )
    }

    const scrollTween = gsap.fromTo(
      text,
      { xPercent: 50 },
      {
        ease: 'none',
        scrollTrigger: {
          end: HorizontalWordsConfig.scrollTween.end,
          scrub: HorizontalWordsConfig.scrollTween.scrub,
          start: getHorizontalWordsScrollStart,
          trigger: section
        },
        xPercent: -100
      }
    )

    if (hasSplitText) this.#animateLetters(gsap, scrollTween, section)
    this.#animateStickers(gsap, scrollTween, section)
    this.#animateArrows(gsap, scrollTween, section, Boolean(DrawSVGPlugin))
  }

  #letterScrollTrigger(scrollTween, trigger) {
    return {
      containerAnimation: scrollTween,
      end: HorizontalWordsConfig.chars.end,
      scrub: HorizontalWordsConfig.chars.scrub,
      start: HorizontalWordsConfig.chars.start,
      trigger
    }
  }

  #animateLetters(gsap, scrollTween, section) {
    const colorProgress = HorizontalWordsConfig.color.progress
    const colorEase = HorizontalWordsConfig.color.ease
    const colorDurations = {
      toNormal: 1 - colorProgress.variantRampEnd,
      toVariant: colorProgress.variantRampEnd
    }

    for (const letter of section.querySelectorAll('.letter')) {
      gsap.from(letter, {
        ease: 'elastic.out(1.2, 1)',
        rotation:
          (Math.random() - 0.5) * HorizontalWordsConfig.random.letterRotation,
        scrollTrigger: this.#letterScrollTrigger(scrollTween, letter),
        yPercent: (Math.random() - 0.5) * HorizontalWordsConfig.random.letterY
      })

      const normalColor = window.getComputedStyle(letter).color
      const variantColor = `var(--variant, ${normalColor})`
      const colorTween = gsap.timeline({
        scrollTrigger: this.#letterScrollTrigger(scrollTween, letter)
      })

      colorTween.to(letter, {
        color: variantColor,
        duration: colorDurations.toVariant,
        ease: colorEase.toVariant
      })
      colorTween.to(letter, {
        color: normalColor,
        duration: colorDurations.toNormal,
        ease: colorEase.toNormal
      })
    }
  }

  #animateStickers(gsap, scrollTween, section) {
    const stickers = section.querySelectorAll('[data-scroll-animation-target]')

    useStickerReveal(gsap, stickers, {
      ease: 'elastic.out(1.2, 1)',
      rotationRange: HorizontalWordsConfig.random.stickerRotation / 2,
      scrollTrigger: (sticker) =>
        this.#letterScrollTrigger(scrollTween, sticker),
      xPercentRange: 0,
      yPercentRange: HorizontalWordsConfig.random.stickerY / 2
    })
  }

  #animateArrows(gsap, scrollTween, section, hasDrawSvg) {
    const arrows = section.querySelectorAll(
      '.horizontal-words__arrow-svg path, .horizontal-words__arrow-end-svg path'
    )

    for (const arrow of arrows) {
      const vars = {
        scrollTrigger: {
          containerAnimation: scrollTween,
          end: 'left 30%',
          scrub: HorizontalWordsConfig.chars.scrub,
          start: HorizontalWordsConfig.chars.start,
          trigger: arrow
        }
      }

      if (hasDrawSvg) {
        vars.drawSVG = '0% 0%'
        vars.duration = 1
      } else {
        vars.autoAlpha = 0
        vars.duration = 0.2
      }

      gsap.from(arrow, vars)
    }
  }

  #cleanup() {
    this.#gsapContext?.revert()
    this.#gsapContext = null

    for (const split of this.#splitInstances) {
      split?.revert?.()
    }
    this.#splitInstances = []
  }
}

if (!customElements.get('horizontal-words-section')) {
  customElements.define('horizontal-words-section', HorizontalWordsSection)
}
